import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  FormGroup,
  FormLabel,
  FormError,
  RadioCard,
  RadioGroup,
  Button,
  FileUploader,
  SwitchInput,
  SwitchTag,
  SwitchLabel,
  CurrencyInput,
  TabGroup,
  Tab,
} from "@components";
import {
  replaceTokens,
  formatAmount,
  normalizeMobile,
  isValidMobile,
  isValidEmail,
  invoicesRequestedTokenMap,
} from "@utils";
import {
  buildRequests,
  buildTokensForRow,
  getCurrentInvoicesRequestedTotal,
} from "./PaymentRequestDrawers/utils";
import { useTemplates } from "./PaymentRequestDrawers/hooks";
import {
  MessageTemplateFields,
  PreviewModal,
  InvoiceRequestTypeSelector,
  PaymentProfileFormField,
} from "./PaymentRequestDrawers/components";
import { createPaymentRequest } from "@api";
import { usePdfStatements } from "@flags";
import { usePaymentSettings } from "@auth";

const SingleRequestDrawer = ({ isOpen, onClose, customer, onComplete, elevated = false }) => {
  const pdfStatements = usePdfStatements();
  const { paymentSettings } = usePaymentSettings();
  const { templates, loading: templatesLoading } = useTemplates();
  const [isTypeInvoice, setIsTypeInvoice] = useState(true);
  const [files, setFiles] = useState(null);

  const { corporateCardSurchargePercent = null } = paymentSettings ?? {};
  const paymentRequestsAllowCardDefault =
    paymentSettings?.paymentRequests?.allowCard ?? false;

  const {
    control,
    getValues,
    watch,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: "",
      invoicesRequested: "Overdue", // None, Overdue, OverdueAndDueThisMonth, All
      invoicesRequestedAreMandatory: false,
      includePdfStatement: false,
      subject: "",
      emailBody: "",
      smsMessage: "",
      template: "none",
      email: customer?.email || "",
      phone: customer?.phone || "",
      allowCardPayment: false, // Default to false, will be updated when user loads
      paymentProfileId: "",
    },
  });

  // Update form values once user data is fetched
  useEffect(() => {
    if (paymentSettings) {
      setValue("allowCardPayment", paymentRequestsAllowCardDefault, {
        shouldValidate: false,
      });
    }
  }, [paymentSettings, paymentRequestsAllowCardDefault, setValue]);

  const invoicesRequested = watch("invoicesRequested");
  const templateId = watch("template");

  const corporateCardSurchargePercentValue =
    !!corporateCardSurchargePercent && corporateCardSurchargePercent > 0
      ? corporateCardSurchargePercent
      : null;

  // Ensure invoicesRequestedAreMandatory is always false when no invoices are mandatory
  useEffect(() => {
    if (invoicesRequested === "None") {
      setValue("invoicesRequestedAreMandatory", false, {
        shouldValidate: false,
      });
    }
  }, [invoicesRequested, setValue]);

  useEffect(() => {
    clearErrors(["emailBody", "smsMessage"]);
  }, [isTypeInvoice, clearErrors]);

  /* ---------------- derived single preparedRow --------------- */
  const preparedRow = useMemo(
    () => (customer ? buildRequests([customer], invoicesRequested)[0] : null),
    [customer, invoicesRequested],
  );

  useEffect(() => {
    if (!preparedRow) return;
    setValue("phone", preparedRow.phone || "", { shouldValidate: true });
    setValue("email", preparedRow.email || "", { shouldValidate: true });
  }, [preparedRow, setValue]);

  /* ---------------- states & toggles ------------------------- */
  const [smsSelected, setSmsSelected] = useState(false);
  const [emailSelected, setEmailSelected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!preparedRow) return;

    const smsOk = isValidMobile(preparedRow?.phone);
    const emailOk = isValidEmail(preparedRow?.email);

    setSmsSelected(smsOk);
    setEmailSelected(!smsOk && emailOk);
  }, [preparedRow, isValidMobile, isValidEmail]);

  /* ---------------- template autofill --------------- */
  useEffect(() => {
    if (!templates || templatesLoading) return;
    if (templateId === "none") {
      setValue("subject", "", { shouldValidate: true });
      setValue("smsMessage", "", { shouldValidate: true });
      setValue("emailBody", "", { shouldValidate: true });
      return;
    }
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl) {
      setValue("subject", tpl.emailSubject || "", { shouldValidate: true });
      setValue("smsMessage", tpl.smsMessage || "", { shouldValidate: true });
      setValue("emailBody", tpl.emailBody || "", { shouldValidate: true });
      clearErrors(["subject", "smsMessage", "emailBody"]);
    }
  }, [templateId, templates, setValue, clearErrors, templatesLoading]);

  /* ---------------- preview modal ------------------- */
  const [showPreview, setShowPreview] = useState(false);

  const getRequestAmount = () =>
    isTypeInvoice
      ? getCurrentInvoicesRequestedTotal(
          preparedRow,
          getValues("invoicesRequested"),
        )
      : getValues("amount");

  const getInvoicesRequested = () =>
    isTypeInvoice
      ? invoicesRequestedTokenMap[getValues("invoicesRequested")]
      : null;

  const tokenValues = useMemo(
    () =>
      preparedRow
        ? buildTokensForRow(
            preparedRow,
            formatAmount(getRequestAmount()),
            getInvoicesRequested(),
          )
        : [],
    [preparedRow, getInvoicesRequested, getRequestAmount, isTypeInvoice],
  );

  /* ---------------- submit & send --------------------------- */
  const onSubmit = async (formData) => {
    if ((!smsSelected && !emailSelected) || !preparedRow) return;
    setSubmitting(true);
    setSubmitError(null);

    const rawPhone = normalizeMobile(formData.phone);
    const rawEmail = formData.email.trim();

    const phone = smsSelected && isValidMobile(rawPhone) ? rawPhone : null;
    const email = emailSelected && isValidEmail(rawEmail) ? rawEmail : null;

    const templateName =
      templates.find((t) => t.id === templateId)?.name ?? null;

    try {
      await createPaymentRequest({
        amountRequested: getRequestAmount(),
        invoicesRequested: isTypeInvoice ? formData.invoicesRequested : null,
        allowAdditionalInvoices: isTypeInvoice ? true : null,
        invoicesRequestedAreMandatory: isTypeInvoice
          ? invoicesRequested === "None"
            ? false
            : formData.invoicesRequestedAreMandatory
          : null,
        includePdfStatement: isTypeInvoice ? formData.includePdfStatement : null,
        phone,
        email,
        communicationMethod: phone && email ? "SmsEmail" : phone ? "Sms" : "Email",
        reference: preparedRow.reference,
        customerName: preparedRow.customerName,
        customerId: preparedRow.customerId,
        subject: replaceTokens(tokenValues, formData.subject),
        message: replaceTokens(tokenValues, formData.smsMessage),
        emailBody: replaceTokens(tokenValues, formData.emailBody),
        payerBaseUrl: window.location.origin.replace("portal", "webpay") + "/pay",
        files,
        allowCardPayment: formData.allowCardPayment,
        paymentProfileId: formData.paymentProfileId || null,
        allowKlarnaPayment: false,
        messageTemplateId: templateId && templateId !== "none" ? templateId : null,
        messageTemplateName: templateName ?? null,
        emailContentFormat: "HtmlSingleBlock",
      });

      onComplete?.();
      closeDrawer();
    } catch (error) {
      console.error(error);
      setSubmitError(
        "Something went wrong sending the payment request. Please check your details and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- drawer close ---------------------------- */
  const closeDrawer = () => {
    reset();
    // After reset, restore the correct allowCardPayment value based on user settings
    if (paymentSettings) {
      setValue("allowCardPayment", paymentRequestsAllowCardDefault, {
        shouldValidate: false,
      });
    }
    setShowPreview(false);
    setSmsSelected(false);
    setEmailSelected(false);
    setSubmitError(null);
    setFiles(null);
    setIsTypeInvoice(true);
    onClose?.();
  };

  return (
    <Drawer title="Payment request" isOpen={isOpen} toggle={closeDrawer} elevated={elevated}>
      <DrawerBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup className="p-5 mb-0">
            <TabGroup className="my-0">
              <Tab
                active={isTypeInvoice}
                onClick={() => setIsTypeInvoice(true)}
              >
                Settle invoices
              </Tab>
              <Tab
                active={!isTypeInvoice}
                onClick={() => setIsTypeInvoice(false)}
              >
                One-time payment
              </Tab>
            </TabGroup>
          </FormGroup>

          <FormGroup className="p-5 pt-2.5 border-b mb-0">
            {isTypeInvoice ? (
              <>
                <FormLabel>Which invoices would you like to be paid?</FormLabel>
                {preparedRow && (
                  <InvoiceRequestTypeSelector
                    control={control}
                    overdueAmount={preparedRow.overdue}
                    dueThisMonthAmount={preparedRow.dueThisMonth}
                    allInvoicesAmount={preparedRow.balance}
                  />
                )}
                <FormError
                  name="invoicesRequested"
                  className="static p-1"
                  errors={errors}
                />
                {invoicesRequested !== "None" && (
                  <SwitchInput
                    className="mt-6"
                    name="invoicesRequestedAreMandatory"
                    control={control}
                  >
                    <SwitchLabel>Make these invoices mandatory</SwitchLabel>
                    <SwitchTag>
                      If enabled, customers must pay these invoices at a minimum
                    </SwitchTag>
                  </SwitchInput>
                )}
              </>
            ) : (
              <>
                <FormLabel>Amount</FormLabel>
                <CurrencyInput
                  name="amount"
                  control={control}
                  placeholder="Enter amount e.g. 50.00"
                  required
                  min={0.3}
                  step={0.01}
                  errors={errors.amount}
                  rules={{
                    min: {
                      value: 0.3,
                      message: "Amount too small (£0.30+)",
                    },
                  }}
                />
              </>
            )}
          </FormGroup>

          {isOpen && (
            <FormGroup className="p-5 border-b mb-0">
              <PaymentProfileFormField
                control={control}
                setValue={setValue}
                corporateCardSurchargePercentValue={corporateCardSurchargePercentValue}
              />
            </FormGroup>
          )}

          {pdfStatements && isTypeInvoice && (
            <FormGroup className="p-5 border-b mb-0">
              <SwitchInput
                className="mt-0"
                name="includePdfStatement"
                control={control}
              >
                <SwitchLabel>Attach statement</SwitchLabel>
                <SwitchTag>
                  If enabled, customers will also receive a current PDF
                  statement via email only
                </SwitchTag>
              </SwitchInput>
            </FormGroup>
          )}

          <FormGroup className="p-5 border-b mb-0">
            <FormLabel>Channels</FormLabel>
            <RadioGroup className="mt-4 flex gap-3">
              <RadioCard
                title="SMS"
                checked={smsSelected}
                onClick={() => setSmsSelected(!smsSelected)}
              />
              <RadioCard
                title="Email"
                checked={emailSelected}
                onClick={() => setEmailSelected(!emailSelected)}
              />
            </RadioGroup>
          </FormGroup>

          {emailSelected && (
            <FormGroup className="p-5 border-b mb-0">
              <FormLabel>Email Attachments</FormLabel>
              <FileUploader name="files" maxFiles={200} onChange={setFiles} />
            </FormGroup>
          )}

          <MessageTemplateFields
            control={control}
            errors={errors}
            templates={templates}
            smsSelected={smsSelected}
            emailSelected={emailSelected}
            isSingleRequest
            isInvoice={isTypeInvoice}
          />
          {submitError && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mx-5 mt-5">
              {submitError}
            </p>
          )}
        </form>
      </DrawerBody>

      <DrawerFooter>
        <Button
          type="button"
          onClick={() => setShowPreview(true)}
          disabled={submitting || (!smsSelected && !emailSelected)}
        >
          Preview
        </Button>
        <Button
          className="ml-auto"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          submitting={submitting}
          disabled={!smsSelected && !emailSelected}
        >
          Send
        </Button>
      </DrawerFooter>

      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        smsSelected={smsSelected}
        emailSelected={emailSelected}
        smsMessage={replaceTokens(tokenValues, getValues("smsMessage"))}
        emailMessage={replaceTokens(tokenValues, getValues("emailBody"))}
        requestedAmount={formatAmount(getRequestAmount())}
        invoicesRequested={getInvoicesRequested()}
        isPlainText={false}
        customerName={preparedRow?.customerName || customer?.name}
        accountCode={preparedRow?.reference || customer?.accountCode}
      />
    </Drawer>
  );
};

export default SingleRequestDrawer;
